class FabricationCalculator extends HTMLElement {
  static selectors = {
    fabrication: 'data-fabrication',
  };

  constructor() {
    super();

    // Cache data attributes early
    this.fabrication = this.getAttribute(
      FabricationCalculator.selectors.fabrication
    );
  }

  connectedCallback() {
    this.processFabricationData();
  }

  fixMixedPercentages(nums) {
    const n = nums.length;

    // Step 1: Determine which values are ambiguous
    const options = nums.map((num) => {
      if (num > 100) {
        // Numbers >100 definitely need decimal conversion
        return [num / 10];
      } else if (num >= 10 && num % 10 === 0) {
        // Numbers like 10, 20, 30, etc. could be either (ambiguous)
        return [num, num / 10];
      } else if (num < 10) {
        // Single digits are likely already correct
        return [num];
      } else {
        // Numbers like 95, 155, etc. - try both original and decimal
        return [num, num / 10];
      }
    });

    let exactMatch = null;
    let bestMatch = null;
    let bestDifference = Infinity;

    const dfs = (index, path, sum) => {
      if (exactMatch) return; // Early exit if we found exact match

      if (index === n) {
        const difference = Math.abs(sum - 100);

        // 1e-6 is a small threshold to consider as "exact"
        // This helps avoid floating point precision issues
        // 1e-6 = 0.000001
        if (difference < 1e-6) {
          exactMatch = [...path];
        } else if (difference < bestDifference) {
          bestMatch = [...path];
          bestDifference = difference;
        }
        return;
      }

      for (let val of options[index]) {
        dfs(index + 1, [...path, val], sum + val);
      }
    };

    dfs(0, [], 0);

    let result;
    if (exactMatch) {
      result = exactMatch;
    } else if (bestMatch) {
      const bestSum = bestMatch.reduce((a, b) => a + b, 0);
      result = bestMatch;
    } else {
      return null;
    }

    // Format output nicely (remove .0)
    const formatted = result.map((x) =>
      x % 1 === 0 ? x : parseFloat(x.toFixed(1))
    );

    return formatted;
  }

  processFabrication(fabricationString) {
    if (!fabricationString) {
      return null;
    }

    const parts = fabricationString.split('-');

    const numbers = [];
    const names = [];
    let currentNumber = null;

    // Parse parts to extract numbers and their corresponding fabric names
    for (const part of parts) {
      const isNumber = /^\d+$/.test(part);

      if (isNumber) {
        // If we have a pending number, pair it with collected names
        if (currentNumber !== null) {
          numbers.push(currentNumber);
          // If no names collected yet, this is an error in the format
          if (names.length < numbers.length) {
            names.push('Unknown');
          }
        }
        currentNumber = parseInt(part);
      } else {
        // Add fabric name
        if (currentNumber !== null) {
          numbers.push(currentNumber);
          names.push(part.charAt(0).toUpperCase() + part.slice(1));
          currentNumber = null;
        }
      }
    }

    // Handle last number if exists
    if (currentNumber !== null) {
      numbers.push(currentNumber);
      names.push('Unknown');
    }

    if (numbers.length === 0) {
      return null;
    }

    // Process all numbers together to find valid percentage combination
    const fixedNumbers = this.fixMixedPercentages(numbers);

    if (!fixedNumbers) {
      return null;
    }

    // Build the formatted result
    const result = fixedNumbers
      .map((percentage, index) => {
        const name = names[index] || 'Unknown';
        return `${percentage}% ${name}`;
      })
      .join(', ');

    return result;
  }

  processFabricationData() {
    const result = this.processFabrication(this.fabrication || this.getAttribute(FabricationCalculator.selectors.fabrication));

    if (!result) {
      return;
    }

    this.innerHTML = result;
  }
}

if (!customElements.get('fabrication-calculator')) {
  customElements.define('fabrication-calculator', FabricationCalculator);
}

